import { assetService } from "@/services/api/asset-service";
import { onboardingService } from "@/services/api/onboarding-service";
import { userGroupService } from "@/services/api/user-group-service";
import { userService } from "@/services/api/user-service";
import type { CreateAssetRequest } from "@/types/api/asset";
import type {
	ApplyStarterPackRequest,
	AssetImportRequest,
	ChecklistImportRequest,
	ClearSmokeTestRequest,
	ClearSmokeTestResult,
	CreateIssueRoleAssignmentRequest,
	CreateTenantLocationRequest,
	ResetClientConfigurationRequest,
	ResetClientConfigurationResult,
	ResetMode,
} from "@/types/api/onboarding";
import type { CreateUserRequest } from "@/types/api/user";
import {
	type QueryClient,
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

/**
 * React-query surface for the Tenant Setup console. Keys are namespaced
 * `['onboarding', clientId, ...]` so a client switch never bleeds cached
 * readiness / invitation data between tenants, and every mutation
 * invalidates the setup-status gates (they are computed from the same rows
 * the mutations write). The industry catalog lives in `use-industries.ts`.
 *
 * The tenant-proxy queries (locations, assets, users, checklists, …) are
 * keyed by clientId as well even though the request itself is scoped by the
 * `X-Tenant-ID` header — the header follows the selected client, so the key
 * and the header always agree.
 */
export const ONBOARDING_KEYS = {
	all: (clientId: string) => ["onboarding", clientId] as const,
	setupStatus: (clientId: string) =>
		["onboarding", clientId, "setup-status"] as const,
	invitations: (clientId: string) =>
		["onboarding", clientId, "invitations"] as const,
	locations: (clientId: string) =>
		["onboarding", clientId, "locations"] as const,
	assetGroups: (clientId: string) =>
		["onboarding", clientId, "asset-groups"] as const,
	assets: (clientId: string) => ["onboarding", clientId, "assets"] as const,
	users: (clientId: string) => ["onboarding", clientId, "users"] as const,
	userGroups: (clientId: string) =>
		["onboarding", clientId, "user-groups"] as const,
	userGroup: (clientId: string, userGroupId: string) =>
		["onboarding", clientId, "user-groups", userGroupId] as const,
	checklists: (clientId: string) =>
		["onboarding", clientId, "checklists"] as const,
	inspectionConfigurations: (clientId: string) =>
		["onboarding", clientId, "inspection-configurations"] as const,
	issueRoles: (clientId: string) =>
		["onboarding", clientId, "issue-roles"] as const,
	issueRole: (clientId: string, roleId: string) =>
		["onboarding", clientId, "issue-roles", roleId] as const,
	issueRoleAssignments: (clientId: string, roleId: string) =>
		["onboarding", clientId, "issue-roles", roleId, "assignments"] as const,
	issueTypes: (clientId: string) =>
		["onboarding", clientId, "issue-types"] as const,
};

/**
 * Legacy page-level keys (Assets / Locations / Users / Groups pages) that
 * show the same tenant rows — invalidated alongside the stepper keys so the
 * flat pages do not show stale lists after a stepper mutation.
 */
const LEGACY_TENANT_KEYS: readonly (readonly string[])[] = [
	["client-assets"],
	["client-locations-list"],
	["client-asset-groups-list"],
	["client-locations"],
	["client-asset-groups"],
	["client-users"],
	["user-groups"],
	["users"],
	["locations"],
	["assets"],
];

function useInvalidateOnboarding(clientId: string) {
	const qc = useQueryClient();
	return (...keys: readonly (readonly unknown[])[]) => {
		qc.invalidateQueries({
			queryKey: ONBOARDING_KEYS.setupStatus(clientId),
		});
		for (const key of keys) {
			qc.invalidateQueries({ queryKey: key });
		}
		for (const key of LEGACY_TENANT_KEYS) {
			qc.invalidateQueries({ queryKey: key });
		}
	};
}

// ────────────────────────────────────────────────────────────────────────────
// Readiness
// ────────────────────────────────────────────────────────────────────────────

export function useSetupStatus(clientId: string | null | undefined) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.setupStatus(clientId ?? ""),
		queryFn: () => onboardingService.getSetupStatus(clientId ?? ""),
		enabled: !!clientId,
	});
}

// ────────────────────────────────────────────────────────────────────────────
// Starter pack
// ────────────────────────────────────────────────────────────────────────────

export function useApplyStarterPack(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: (data?: ApplyStarterPackRequest) =>
			onboardingService.applyStarterPack(clientId, data),
		onSettled: () => {
			invalidate(
				ONBOARDING_KEYS.checklists(clientId),
				ONBOARDING_KEYS.assetGroups(clientId),
				ONBOARDING_KEYS.inspectionConfigurations(clientId),
				ONBOARDING_KEYS.userGroups(clientId),
				ONBOARDING_KEYS.issueRoles(clientId),
				ONBOARDING_KEYS.issueTypes(clientId),
			);
		},
	});
}

// ────────────────────────────────────────────────────────────────────────────
// Checklist JSON import
// ────────────────────────────────────────────────────────────────────────────

export function useImportChecklistJson(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: ({
			payload,
			dryRun,
		}: {
			payload: ChecklistImportRequest;
			dryRun: boolean;
		}) => onboardingService.importChecklistJson(clientId, payload, dryRun),
		onSettled: (_data, _error, variables) => {
			if (!variables.dryRun) {
				// A wired import also creates the asset group, configuration,
				// inspector group and notification rules.
				invalidate(
					ONBOARDING_KEYS.checklists(clientId),
					ONBOARDING_KEYS.issueTypes(clientId),
					ONBOARDING_KEYS.assetGroups(clientId),
					ONBOARDING_KEYS.inspectionConfigurations(clientId),
					ONBOARDING_KEYS.userGroups(clientId),
					ONBOARDING_KEYS.issueRoles(clientId),
				);
			}
		},
	});
}

// ────────────────────────────────────────────────────────────────────────────
// Invitations
// ────────────────────────────────────────────────────────────────────────────

export function useHeldInvitations(clientId: string | null | undefined) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.invitations(clientId ?? ""),
		queryFn: () => onboardingService.getHeldInvitations(clientId ?? ""),
		enabled: !!clientId,
	});
}

export function useSendInvitations(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: (clientUserIds: string[]) =>
			onboardingService.sendInvitationBatch(clientId, clientUserIds),
		onSettled: () => {
			invalidate(
				ONBOARDING_KEYS.invitations(clientId),
				ONBOARDING_KEYS.users(clientId),
			);
		},
	});
}

// ────────────────────────────────────────────────────────────────────────────
// Reset configuration (Phase 1B)
// ────────────────────────────────────────────────────────────────────────────

/**
 * After a destructive tenant-wide procedure (reset, clear smoke-test data)
 * every tenant-scoped row may have changed: drop every cached query — the
 * stepper keys (setup-status, assets, users, invitations, checklists,
 * configurations, …), the legacy page keys and the client record — except
 * the admin's own context and the global catalogs. Resolves once every
 * *active* matching query has re-fetched (`invalidateQueries` awaits the
 * refetches it starts).
 */
function invalidateAllTenantQueries(qc: QueryClient): Promise<void> {
	return qc.invalidateQueries({
		predicate: (query) => {
			const head = query.queryKey[0];
			return head !== "admin-user-context" && head !== "industries";
		},
	});
}

/**
 * Called from the `mutationFn` of the two destructive procedures after the
 * server confirmed an Execute, so the mutation stays `isPending` until the
 * lists on screen are fresh — a card that closes its dialog and clears its
 * selection on success must never do so against a list that still shows
 * the removed rows.
 *
 * `invalidateAllTenantQueries` starts the refetch of every active tenant
 * query synchronously; the explicit `refetchQueries` calls join those
 * in-flight requests (`cancelRefetch: false`) rather than restarting them,
 * and additionally re-read the lists the stepper renders when their step is
 * not mounted (assets, checklists, configurations) so the next step opens
 * fresh. Refetch failures never reject — the procedure itself succeeded and
 * the card reports that; a failed list shows its own error.
 */
async function refetchTenantQueriesAfterExecute(
	qc: QueryClient,
	clientId: string,
): Promise<void> {
	const explicitKeys = [
		ONBOARDING_KEYS.users(clientId),
		ONBOARDING_KEYS.invitations(clientId),
		ONBOARDING_KEYS.setupStatus(clientId),
		ONBOARDING_KEYS.assets(clientId),
		ONBOARDING_KEYS.checklists(clientId),
		ONBOARDING_KEYS.inspectionConfigurations(clientId),
	];
	await Promise.all([
		invalidateAllTenantQueries(qc),
		...explicitKeys.map((queryKey) =>
			qc.refetchQueries({ queryKey }, { cancelRefetch: false }),
		),
	]);
}

interface DestructiveProcedureCallbacks<TResult> {
	/**
	 * Fires once an Execute has completed on the server AND the tenant lists
	 * have been re-read. Lives on the hook (not on a per-call `mutate(vars,
	 * { onSuccess })`) because TanStack v5 only runs per-call callbacks while
	 * the calling component is still mounted (subscribed) when the mutation
	 * settles — hook-level callbacks always run.
	 */
	onExecuted?: (result: TResult) => void;
	/** Same guarantee for a Preview (the card's Execute button gates on it). */
	onPreviewed?: (result: TResult) => void;
}

export function useResetClientConfiguration(
	clientId: string,
	{
		onExecuted,
		onPreviewed,
	}: DestructiveProcedureCallbacks<ResetClientConfigurationResult> = {},
) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			mode,
			...body
		}: { mode: ResetMode } & ResetClientConfigurationRequest) => {
			const result = await onboardingService.resetClientConfiguration(
				clientId,
				mode,
				body,
			);
			if (result.executed) {
				await refetchTenantQueriesAfterExecute(qc, clientId);
			}
			return result;
		},
		onSuccess: (data) => {
			if (data.executed) onExecuted?.(data);
			else onPreviewed?.(data);
		},
	});
}

// ────────────────────────────────────────────────────────────────────────────
// Clear smoke-test data (Go-live step)
// ────────────────────────────────────────────────────────────────────────────

export function useClearSmokeTestData(
	clientId: string,
	{
		onExecuted,
		onPreviewed,
	}: DestructiveProcedureCallbacks<ClearSmokeTestResult> = {},
) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({
			mode,
			...body
		}: { mode: ResetMode } & ClearSmokeTestRequest) => {
			const result = await onboardingService.clearSmokeTestData(
				clientId,
				mode,
				body,
			);
			if (result.executed) {
				await refetchTenantQueriesAfterExecute(qc, clientId);
			}
			return result;
		},
		onSuccess: (data) => {
			if (data.executed) onExecuted?.(data);
			else onPreviewed?.(data);
		},
	});
}

// ────────────────────────────────────────────────────────────────────────────
// Tenant lists (proxies, x-tenant-id)
// ────────────────────────────────────────────────────────────────────────────

export function useTenantLocations(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.locations(clientId),
		queryFn: () => onboardingService.getTenantLocations(),
		enabled: !!clientId,
	});
}

export function useCreateTenantLocation(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: (data: CreateTenantLocationRequest) =>
			onboardingService.createTenantLocation(data),
		onSettled: () => invalidate(ONBOARDING_KEYS.locations(clientId)),
	});
}

export function useTenantAssetGroups(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.assetGroups(clientId),
		queryFn: () => onboardingService.getTenantAssetGroups(),
		enabled: !!clientId,
	});
}

export function useTenantAssets(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.assets(clientId),
		queryFn: () => onboardingService.getTenantAssets(),
		enabled: !!clientId,
	});
}

/** Same `POST /assets` proxy the admin Assets page uses (asset-service). */
export function useCreateTenantAsset(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: (data: Omit<CreateAssetRequest, "clientId">) =>
			assetService.createAsset(data),
		onSettled: () =>
			invalidate(
				ONBOARDING_KEYS.assets(clientId),
				ONBOARDING_KEYS.assetGroups(clientId),
				ONBOARDING_KEYS.locations(clientId),
			),
	});
}

/**
 * Asset spreadsheet import. A dry run writes nothing, so only an execute
 * invalidates the asset / group / location lists and the readiness gates.
 */
export function useImportAssets(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: ({
			payload,
			dryRun,
		}: {
			payload: AssetImportRequest;
			dryRun: boolean;
		}) => onboardingService.importAssets(clientId, payload, dryRun),
		onSettled: (_data, _error, variables) => {
			if (!variables.dryRun) {
				invalidate(
					ONBOARDING_KEYS.assets(clientId),
					ONBOARDING_KEYS.assetGroups(clientId),
					ONBOARDING_KEYS.locations(clientId),
				);
			}
		},
	});
}

export function useTenantUsers(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.users(clientId),
		queryFn: () => onboardingService.getTenantUsers(),
		enabled: !!clientId,
	});
}

/** Same `POST /users` proxy the admin Create User page uses (held by default). */
export function useCreateTenantUser(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: (data: Omit<CreateUserRequest, "clientId">) =>
			userService.createUser({ sendInvitation: false, ...data }),
		onSettled: () =>
			invalidate(
				ONBOARDING_KEYS.users(clientId),
				ONBOARDING_KEYS.invitations(clientId),
			),
	});
}

export function useTenantUserGroups(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.userGroups(clientId),
		queryFn: () => onboardingService.getTenantUserGroups(),
		enabled: !!clientId,
	});
}

/** Creates the starter pack's user groups the client is missing (idempotent). */
export function useEnsureStarterUserGroups(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: () => onboardingService.ensureStarterUserGroups(clientId),
		onSettled: () => invalidate(ONBOARDING_KEYS.userGroups(clientId)),
	});
}

/**
 * Members of every user group (one `GET /usergroups/{id}` per group) — the
 * list endpoint does not project memberships, and the tenant typically has a
 * handful of groups, so N small calls beat N-users calls.
 */
export function useTenantUserGroupDetails(
	clientId: string,
	userGroupIds: string[],
) {
	return useQueries({
		queries: userGroupIds.map((id) => ({
			queryKey: ONBOARDING_KEYS.userGroup(clientId, id),
			queryFn: () => onboardingService.getTenantUserGroup(id),
			enabled: !!clientId,
		})),
	});
}

/** `POST /user-group/{id}/users` (Client `UserGroup/AddUser`). */
export function useAddUserToGroup(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: ({
			userGroupId,
			clientUserId,
		}: {
			userGroupId: string;
			clientUserId: string;
		}) => userGroupService.addUser(userGroupId, clientUserId),
		onSettled: (_data, _error, variables) =>
			invalidate(
				ONBOARDING_KEYS.userGroups(clientId),
				ONBOARDING_KEYS.userGroup(clientId, variables.userGroupId),
				ONBOARDING_KEYS.inspectionConfigurations(clientId),
			),
	});
}

export function useTenantChecklists(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.checklists(clientId),
		queryFn: () => onboardingService.getTenantChecklists(),
		enabled: !!clientId,
	});
}

export function useTenantInspectionConfigurations(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.inspectionConfigurations(clientId),
		queryFn: () => onboardingService.getTenantInspectionConfigurations(),
		enabled: !!clientId,
	});
}

export function useIssueRoles(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.issueRoles(clientId),
		queryFn: () => onboardingService.getIssueRoles(),
		enabled: !!clientId,
	});
}

/** One role incl. its notification rules (the issue types that notify it). */
export function useIssueRole(clientId: string, roleId: string, enabled = true) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.issueRole(clientId, roleId),
		queryFn: () => onboardingService.getIssueRole(roleId),
		enabled: enabled && !!clientId && !!roleId,
	});
}

export function useIssueRoleAssignments(clientId: string, roleId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.issueRoleAssignments(clientId, roleId),
		queryFn: () => onboardingService.getIssueRoleAssignments(roleId),
		enabled: !!clientId && !!roleId,
	});
}

export function useCreateIssueRoleAssignment(clientId: string) {
	const invalidate = useInvalidateOnboarding(clientId);
	return useMutation({
		mutationFn: ({
			roleId,
			data,
		}: {
			roleId: string;
			data: CreateIssueRoleAssignmentRequest;
		}) => onboardingService.createIssueRoleAssignment(roleId, data),
		onSettled: (_data, _error, variables) =>
			invalidate(
				ONBOARDING_KEYS.issueRoles(clientId),
				ONBOARDING_KEYS.issueRoleAssignments(
					clientId,
					variables.roleId,
				),
			),
	});
}

export function useTenantIssueTypes(clientId: string) {
	return useQuery({
		queryKey: ONBOARDING_KEYS.issueTypes(clientId),
		queryFn: () => onboardingService.getTenantIssueTypes(),
		enabled: !!clientId,
	});
}
